import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckSquare, CheckCircle2, RefreshCw, FileText, ThumbsUp, ThumbsDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_COLORS = {
  critical: 'bg-red-100 border-red-300 text-red-800',
  high: 'bg-orange-100 border-orange-300 text-orange-800',
  medium: 'bg-blue-100 border-blue-300 text-blue-800',
  low: 'bg-slate-100 border-slate-300 text-slate-800',
};

const PRIORITY_LABELS = {
  critical: 'CRÍTICA',
  high: 'ALTA',
  medium: 'MEDIA',
  low: 'BAJA',
};

const CONTENT_TYPE_LABELS = {
  reel: '🎬 Reel',
  tiktok: '🎵 TikTok',
  email: '📧 Email',
  blog: '📝 Artículo Blog',
  calendario: '📅 Calendario',
  ad: '📣 Anuncio',
  social_post: '📱 Post',
};

const ContentCard = ({ item, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(false);
  const raw = item.content?.raw || '';

  return (
    <div className="p-4 rounded-lg border bg-white shadow-sm border-amber-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-100 border-amber-300 text-amber-800 text-xs border">
              {CONTENT_TYPE_LABELS[item.content_type] || item.content_type}
            </Badge>
            <span className="font-semibold text-slate-900">{item.title}</span>
          </div>
          <p className="text-xs text-slate-500 mb-2">Generado por {item.agent_name} · {new Date(item.timestamp).toLocaleString('es-ES')}</p>

          {expanded ? (
            <pre className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-3 border mt-2 font-mono text-xs">
              {raw}
            </pre>
          ) : (
            <p className="text-sm text-slate-500 line-clamp-2">{raw.substring(0, 150)}...</p>
          )}

          <button
            className="text-xs text-blue-600 hover:underline mt-1"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Ver menos' : 'Ver contenido completo'}
          </button>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700"
            onClick={() => onApprove(item.id)}
          >
            <ThumbsUp className="h-4 w-4 mr-1" /> Aprobar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => onReject(item.id)}
          >
            <ThumbsDown className="h-4 w-4 mr-1" /> Rechazar
          </Button>
        </div>
      </div>
    </div>
  );
};

const TasksPage = ({ tasks, onRefresh, toast }) => {
  const [pendingContent, setPendingContent] = useState([]);

  const loadContent = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/content/pending`);
      setPendingContent(r.data || []);
    } catch (e) {}
  };

  useEffect(() => { loadContent(); }, []);

  const markDone = async (taskId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/tasks/${taskId}/done`);
      toast({ title: '✅ Tarea completada', description: 'Los agentes revisarán el resultado.' });
      onRefresh();
    } catch (e) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const approveContent = async (contentId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/content/${contentId}/approve`);
      toast({ title: '✅ Contenido aprobado' });
      loadContent();
    } catch (e) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const rejectContent = async (contentId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/content/${contentId}/reject`);
      toast({ title: 'Contenido rechazado' });
      loadContent();
    } catch (e) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleRefresh = () => { onRefresh(); loadContent(); };

  const pending = tasks.filter(t => !t.completed);
  const done = tasks.filter(t => t.completed);

  return (
    <div className="space-y-6">
      {/* Contenido pendiente de aprobación */}
      {pendingContent.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-amber-600" /> Contenido para aprobar ({pendingContent.length})
                </CardTitle>
                <CardDescription>
                  Generado por los agentes. Revísalo y aprueba o rechaza.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingContent.map((item, i) => (
                <ContentCard
                  key={i}
                  item={item}
                  onApprove={approveContent}
                  onReject={rejectContent}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tareas */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5" /> Tus tareas
              </CardTitle>
              <CardDescription>
                Lo que tienes que hacer tú. Con instrucciones exactas.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {pending.length === 0 && done.length === 0 ? (
            <p className="text-slate-500 text-center py-12">Sin tareas pendientes. Bien hecho.</p>
          ) : (
            <div className="space-y-4">
              {pending.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 mb-3">Pendientes ({pending.length})</h3>
                  <div className="space-y-3">
                    {pending.map((task, i) => (
                      <div key={i} className="p-4 rounded-lg border bg-white shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={`text-xs border ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>
                                {PRIORITY_LABELS[task.priority] || 'MEDIA'}
                              </Badge>
                              <span className="font-semibold text-slate-900">{task.title}</span>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{task.description}</p>
                            {task.why && (
                              <p className="text-xs text-slate-500 italic mb-2">Por qué: {task.why}</p>
                            )}
                            {task.instructions && task.instructions.length > 0 && (
                              <div className="mt-2 p-3 bg-slate-50 rounded border">
                                <p className="text-xs font-semibold text-slate-600 mb-1">Cómo hacerlo:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                  {task.instructions.map((step, j) => (
                                    <li key={j} className="text-sm text-slate-700">{step}</li>
                                  ))}
                                </ol>
                              </div>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(task.timestamp).toLocaleString('es-ES')}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 shrink-0"
                            onClick={() => markDone(task.id)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Ya realizado
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {done.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-400 mb-3 mt-6">Completadas ({done.length})</h3>
                  <div className="space-y-2 opacity-60">
                    {done.slice(0, 10).map((task, i) => (
                      <div key={i} className="p-3 rounded-lg border bg-white flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                        <span className="text-sm text-slate-500 line-through">{task.title}</span>
                        <span className="text-xs text-slate-400 ml-auto">
                          {task.completed_at ? new Date(task.completed_at).toLocaleDateString('es-ES') : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TasksPage;
